import React from 'react';
import { Box, Button, Paper, TextField, Typography } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { getInstruction, saveInstruction } from '../../services/aiInstructionsService';

const defaultPrequal = `Instrukcja AI – Prekwalifikacja\n\nCel: Na podstawie dokumentu SWZ (Specyfikacji Warunków Zamówienia) wygeneruj JSON z danymi potrzebnymi do wypełnienia formularza "Dodaj nową ofertę". Model powinien wypełnić WSZYSTKIE pola formularza. Jeśli dane nie są dostępne w dokumencie, w polu należy wpisać "brak danych".\n\nZwracaj WYŁĄCZNIE poprawny JSON (obiekt), bez komentarzy/markdownu, dokładnie w poniższej strukturze:\n\n{\n  "offer_preparer": "__AI__",\n  "status": "Prekwalifikacja",\n  "client_name": "Nazwa zamawiającego",\n  "client_address": "Adres zamawiającego",\n  "order_name": "Pełna nazwa zamówienia",\n  "investment_address": "Adres inwestycji",\n  "location": "Miejscowość",\n  "site_inspection": "Tak" | "Nie",\n  "site_inspection_date": "YYYY-MM-DD" | "",\n  "submission_deadline": "YYYY-MM-DD",\n  "estimated_order_value": liczba,\n  "bid_bond": liczba,\n  "client_website": "URL" | "",\n  "miro_link": "URL" | "",\n  "technical_experience": "Opis doświadczenia technicznego",\n  "personnel_potential": "Opis potencjału osobowego",\n  "economic_situation": "Opis sytuacji ekonomicznej"\n}\n\nSzczegółowe formaty danych:\n\n1. "offer_preparer": zawsze "__AI__" (string)\n\n2. "status": zawsze "Prekwalifikacja" (string, dokładnie ta wartość)\n\n3. "client_name": pełna nazwa zamawiającego (string, np. "Gmina Miasto Warszawa")\n\n4. "client_address": pełny adres zamawiającego (string, format: "ul. Przykładowa 1, 00-000 Warszawa")\n\n5. "order_name": pełny przedmiot umowy (string, może być wielowierszowy, np. "Budowa drogi gminnej wraz z infrastrukturą techniczną")\n\n6. "investment_address": pełny adres inwestycji (string, format: "Al. Jerozolimskie 3, 00-495 Warszawa")\n\n7. "location": nazwa miejscowości (string, np. "Warszawa")\n\n8. "site_inspection": dokładnie "Tak" lub "Nie" (string, wybierz "Tak" tylko jeśli w dokumencie jest wyraźna informacja o wizji lokalnej)\n\n9. "site_inspection_date": data w formacie "YYYY-MM-DD" (string, np. "2024-12-31") - wypełnij TYLKO jeśli "site_inspection" === "Tak", w przeciwnym razie pusty string ""\n\n10. "submission_deadline": termin składania oferty w formacie "YYYY-MM-DD" (string, np. "2024-12-31")\n\n11. "estimated_order_value": szacunkowa wartość zamówienia w PLN (liczba, bez spacji tysięcznych, np. 1500000.50)\n\n12. "bid_bond": wadium w PLN (liczba, bez spacji tysięcznych, np. 100000)\n\n13. "client_website": pełny URL strony zamawiającego (string, np. "https://www.warszawa.pl") lub pusty string "" jeśli brak\n\n14. "miro_link": pełny URL do Miro (string) lub pusty string "" jeśli brak\n\n15. "technical_experience": opis wymagań dotyczących doświadczenia technicznego (string, może być wielowierszowy)\n\n16. "personnel_potential": opis wymagań dotyczących potencjału osobowego (string, może być wielowierszowy)\n\n17. "economic_situation": opis wymagań dotyczących sytuacji ekonomicznej (string, może być wielowierszowy)\n\nWytyczne:\n- Jeśli dane nie są dostępne w dokumencie, wpisz "brak danych" (nie zostawiaj pustych pól)\n- Pola opcjonalne (client_website, miro_link) mogą być pustym stringiem "" jeśli brak danych\n- Wszystkie pola tekstowe muszą zawierać wartość - jeśli brak danych w dokumencie, wpisz "brak danych"\n- Daty zawsze w formacie "YYYY-MM-DD" (rok-miesiąc-dzień)\n- Wartości liczbowe (estimated_order_value, bid_bond) jako liczby, bez spacji tysięcznych, z kropką jako separatorem dziesiętnym jeśli potrzebne\n- "site_inspection_date" wypełnij tylko jeśli "site_inspection" === "Tak", w przeciwnym razie pusty string ""`;
const defaultPackaging = `Instrukcja AI – Pakietowanie prac\n\nCel: Na podstawie przedmiarów robót (PDF/DOCX/TXT) wygeneruj JSON z propozycjami pakietów do kontraktowania. Zwracaj WYŁĄCZNIE poprawny JSON (obiekt lub tablica obiektów), bez komentarzy/markdownu, dokładnie w poniższej strukturze i typach. Liczby bez spacji tysięcznych.\n\nStruktura JSON (dla jednego pakietu):\n{\n  "package_symbol": "SYM",\n  "name": "Nazwa pakietu",\n  "stage": "Etap 1",\n  "package_type": "materiały" ,\n  "description": "",\n  "parameters": "",\n  "estimated_value": 0\n}\n\nWytyczne:\n- "package_symbol" nadaj krótki, unikalny skrót (np. PAC.1).\n- "stage" domyślnie "Etap 1" jeśli brak informacji.\n- "package_type" jedna z: "materiały" | "usługi" | "sprzęt" (dobierz po charakterze pozycji).\n- "estimated_value" oszacuj na podstawie pozycji przedmiaru (jeśli niepewne – 0).\n- Opis i parametry streść syntetycznie (maks. 2–3 zdania).\n- Jeśli tworzysz wiele pakietów, zwróć TABLICĘ takich obiektów.`;

export default function AiInstructionsTab() {
  const [prequal, setPrequal] = React.useState<string>('');
  const [packaging, setPackaging] = React.useState<string>('');
  const [meetingNotes, setMeetingNotes] = React.useState<string>('');
  const [notesTasks, setNotesTasks] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      getInstruction('offers_prequal'),
      getInstruction('packages_packaging'),
      getInstruction('notes_from_transcription'),
      getInstruction('notes_ai_tasks')
    ])
      .then(([a, b, c, d]) => {
        if (!mounted) return;
        setPrequal((a?.content && a.content.trim()) || defaultPrequal);
        setPackaging((b?.content && b.content.trim()) || defaultPackaging);
        const def = [
          'Instrukcja AI – Notatki ze spotkań',
          '',
          'Cel: Na podstawie pełnej transkrypcji przygotuj zwięzłą notatkę po polsku.',
          'Wymagane sekcje: Tytuł, Uczestnicy, Data, Najważniejsze ustalenia (lista punktów),',
          'Zadania (lista: [Osoba] — zadanie — termin, jeśli padł), Ryzyka / Otwarte tematy, Podsumowanie.',
          'Unikaj przepisów treści dosłownie; syntetyzuj.',
        ].join('\\n');
        setMeetingNotes((c?.content && c.content.trim()) || def);
        const defTasks = [
          'Instrukcja AI – Zadania z notatek',
          '',
          'Zwróć WYŁĄCZNIE JSON (tablica obiektów): { "title": string, "due_date": "YYYY-MM-DD" | null }.',
          'Tytuły krótkie i konkretne. Termin ustaw na null, jeśli nie padł w notatce.',
        ].join('\\n');
        setNotesTasks((d?.content && d.content.trim()) || defTasks);
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  async function savePrequal() {
    await saveInstruction('offers_prequal', { content: prequal, title: 'Prekwalifikacja – import ofert', module: 'offers', usage_tag: 'prequal' });
  }

  function resetPrequal() {
    setPrequal(defaultPrequal);
  }

  async function savePackaging() {
    await saveInstruction('packages_packaging', { content: packaging, title: 'Pakietowanie prac – generowanie pakietów', module: 'projects', usage_tag: 'packaging' });
  }

  function resetPackaging() {
    setPackaging(defaultPackaging);
  }

  async function saveMeetingNotes() {
    await saveInstruction('notes_from_transcription', { content: meetingNotes, title: 'Notatki ze spotkań – transkrypcja', module: 'meetings', usage_tag: 'notes' });
  }
  async function saveNotesTasks() {
    await saveInstruction('notes_ai_tasks', { content: notesTasks, title: 'Zadania z notatek – AI', module: 'meetings', usage_tag: 'notes_tasks' });
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SmartToyIcon color="primary" /> Instrukcje AI
      </Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Prekwalifikacja</Typography>
        <TextField
          value={prequal}
          onChange={(e)=>setPrequal(e.target.value)}
          placeholder="Wklej/edytuj prompt instrukcji dla asystenta Prekwalifikacji"
          fullWidth
          multiline
          minRows={10}
          disabled={loading}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button variant="contained" onClick={savePrequal}>Zapisz</Button>
          <Button variant="outlined" onClick={resetPrequal}>Przywróć domyślne</Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Pakietowanie prac</Typography>
        <TextField
          value={packaging}
          onChange={(e)=>setPackaging(e.target.value)}
          placeholder="Wklej/edytuj prompt dla generowania JSON pakietów z przedmiarów"
          fullWidth
          multiline
          minRows={10}
          disabled={loading}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button variant="contained" onClick={savePackaging}>Zapisz</Button>
          <Button variant="outlined" onClick={resetPackaging}>Przywróć domyślne</Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Notatki ze spotkań (z transkrypcji)</Typography>
        <TextField
          value={meetingNotes}
          onChange={(e)=>setMeetingNotes(e.target.value)}
          placeholder="Wklej/edytuj prompt dla generowania notatek ze spotkań na podstawie transkrypcji"
          fullWidth
          multiline
          minRows={10}
          disabled={loading}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button variant="contained" onClick={saveMeetingNotes}>Zapisz</Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Zadania AI z notatek</Typography>
        <TextField
          value={notesTasks}
          onChange={(e)=>setNotesTasks(e.target.value)}
          placeholder='Prompt do wygenerowania zadań jako JSON [{"title":"...","due_date":"YYYY-MM-DD|null"}]'
          fullWidth
          multiline
          minRows={10}
          disabled={loading}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button variant="contained" onClick={saveNotesTasks}>Zapisz</Button>
        </Box>
      </Paper>
    </Box>
  );
}


